import React from 'react';
import { Workflow } from '../../../types/workflow';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-trim rounded-xl p-5">
      <h3 className="text-sm font-semibold text-ink mb-2">{title}</h3>
      <div className="text-sm text-copy">{children}</div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-5 space-y-1">
      {items.map((s, i) => <li key={i}>{s}</li>)}
    </ul>
  );
}

export function LogicTab({ workflow }: { workflow: Workflow }) {
  const l = workflow.logic;
  const ai = l.ai_reasoning;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Section title="Executive summary"><p className="leading-relaxed">{l.executive_summary}</p></Section>
      <Section title="Trigger conditions"><BulletList items={l.trigger_conditions} /></Section>

      <Section title="Step-by-step SOP">
        <ol className="space-y-3">
          {l.sop_steps.map((s, i) => (
            <li key={i}>
              <div className="font-medium text-ink">{s.title}</div>
              <div className="text-copy mt-0.5">{s.detail}</div>
            </li>
          ))}
        </ol>
      </Section>

      <Section title="Decision logic"><BulletList items={l.decision_logic} /></Section>
      <Section title="Exception handling"><BulletList items={l.exception_handling} /></Section>
      <Section title="Human escalation rules"><BulletList items={l.escalation_rules} /></Section>

      <section className="bg-white border border-trim rounded-xl p-5 lg:col-span-2">
        <h3 className="text-sm font-semibold text-ink mb-3">AI reasoning</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-xs text-muted">Model</div>
            <div className="text-sm font-mono text-ink mt-0.5">{ai.model}</div>
          </div>
          <div>
            <div className="text-xs text-muted">Confidence threshold</div>
            <div className="text-sm font-mono text-ink mt-0.5">{ai.confidence_threshold}</div>
          </div>
        </div>

        <div className="mb-4">
          <div className="text-xs text-muted mb-1.5">System prompt excerpt (from <code className="font-mono">domain/ai/agents/manager_agent.py</code>)</div>
          <pre className="text-xs font-mono bg-wash text-copy rounded-lg p-3 overflow-x-auto whitespace-pre-wrap leading-relaxed border border-trim">{ai.system_prompt_excerpt}</pre>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-medium text-ink mb-1.5">Grounding sources</div>
            <BulletList items={ai.grounding_sources} />
          </div>
          <div>
            <div className="text-xs font-medium text-ink mb-1.5">Tool permissions</div>
            <BulletList items={ai.tool_permissions} />
          </div>
          <div>
            <div className="text-xs font-medium text-ink mb-1.5">Safety rules</div>
            <BulletList items={ai.safety_rules} />
          </div>
          <div>
            <div className="text-xs font-medium text-ink mb-1.5">Evaluation metrics</div>
            <BulletList items={ai.evaluation_metrics} />
          </div>
          <div className="md:col-span-2">
            <div className="text-xs font-medium text-ink mb-1.5">Fallback logic</div>
            <p className="text-sm text-copy">{ai.fallback_logic}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
